import {
  type AudioDevicesSnapshot,
  type AudioDevicesSnapshotOptions,
  getAudioDevicesSnapshot,
  getAudioDevicesSnapshotSync,
} from '@/lib/audio-devices-snapshot';
import type { AudioOutputDiagnostics } from '@/lib/audio-output-diagnostics';
import { type NetworkStatusSnapshot, getNetworkStatus } from '@/lib/network-status';

export interface CallDiagnosticsData {
  network: NetworkStatusSnapshot;
  audio: AudioDevicesSnapshot | null;
  timestamp: number;
}

export interface CallDiagnosticsOptions {
  /** The active audio track from an ongoing call, used to get the actual device being used */
  activeAudioTrack?: MediaStreamTrack | null;
}

export async function getCallDiagnostics(
  options?: CallDiagnosticsOptions,
): Promise<CallDiagnosticsData> {
  const network = getNetworkStatus();
  const audioOptions: AudioDevicesSnapshotOptions | undefined = options?.activeAudioTrack
    ? { activeAudioTrack: options.activeAudioTrack }
    : undefined;
  const audio = await getAudioDevicesSnapshot(audioOptions);

  return {
    network,
    audio,
    timestamp: Date.now(),
  };
}

export function getCallDiagnosticsSync(): CallDiagnosticsData {
  const network = getNetworkStatus();
  const audio = getAudioDevicesSnapshotSync();

  return {
    network,
    audio,
    timestamp: Date.now(),
  };
}

export interface NetworkErrorDiagnosticsData {
  network: NetworkStatusSnapshot;
  alloApi: 'reachable' | 'unreachable' | 'timeout' | 'error';
  alloApiLatencyMs: number | null;
  vonageApi: 'reachable' | 'unreachable' | 'timeout' | 'error';
  vonageApiLatencyMs: number | null;
  timestamp: number;
}

export interface MediaTimeoutDiagnosticsData {
  mic: {
    canAcquire: boolean;
    error?: string;
    permissionState: string;
    latencyMs: number;
  };
  ice: {
    canGatherCandidates: boolean;
    candidateTypes: string[];
    gatheringTimeMs: number;
    error?: string;
  };
  network: NetworkStatusSnapshot;
  alloApi: 'reachable' | 'unreachable' | 'timeout' | 'error';
  alloApiLatencyMs: number | null;
  vonageApi: 'reachable' | 'unreachable' | 'timeout' | 'error';
  vonageApiLatencyMs: number | null;
  audioContextState: string;
  timestamp: number;
}

async function probeMic(): Promise<MediaTimeoutDiagnosticsData['mic']> {
  let permissionState = 'error';
  try {
    const perm = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    permissionState = perm.state;
  } catch {
    // permissions API may not be available
  }

  const start = Date.now();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const latencyMs = Date.now() - start;
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return { canAcquire: true, permissionState, latencyMs };
  } catch (e) {
    return {
      canAcquire: false,
      error: e instanceof DOMException ? e.name : e instanceof Error ? e.message : String(e),
      permissionState,
      latencyMs: Date.now() - start,
    };
  }
}

async function probeIce(): Promise<MediaTimeoutDiagnosticsData['ice']> {
  const start = Date.now();
  try {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    const candidateTypes: string[] = [];

    const gatheringDone = new Promise<void>((resolve) => {
      const timeout = setTimeout(() => resolve(), 5000);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const type = event.candidate.type;
          if (type && !candidateTypes.includes(type)) {
            candidateTypes.push(type);
          }
        } else {
          // Gathering complete
          clearTimeout(timeout);
          resolve();
        }
      };
    });

    pc.createDataChannel('probe');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await gatheringDone;
    pc.close();

    return {
      canGatherCandidates: candidateTypes.length > 0,
      candidateTypes,
      gatheringTimeMs: Date.now() - start,
    };
  } catch (e) {
    return {
      canGatherCandidates: false,
      candidateTypes: [],
      gatheringTimeMs: Date.now() - start,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

function probeAudioContext(): string {
  try {
    const ctx = new AudioContext();
    const { state } = ctx;
    ctx.close().catch(() => {});
    return state;
  } catch {
    return 'error';
  }
}

async function probeVonageApi(): Promise<{
  status: NetworkErrorDiagnosticsData['vonageApi'];
  latencyMs: number | null;
}> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const start = Date.now();

    const response = await fetch('https://api-us.vonage.com', {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    // With no-cors, an opaque response (type === 'opaque') means the server responded
    const latencyMs = Date.now() - start;
    const status = response.type === 'opaque' || response.ok ? 'reachable' : 'unreachable';
    return { status, latencyMs };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { status: 'timeout', latencyMs: null };
    }
    return { status: 'error', latencyMs: null };
  }
}

export async function getMediaTimeoutDiagnostics(): Promise<MediaTimeoutDiagnosticsData> {
  const [mic, ice, networkDiag] = await Promise.all([
    probeMic(),
    probeIce(),
    getNetworkDiagnosticsOnError(),
  ]);

  return {
    mic,
    ice,
    network: networkDiag.network,
    alloApi: networkDiag.alloApi,
    alloApiLatencyMs: networkDiag.alloApiLatencyMs,
    vonageApi: networkDiag.vonageApi,
    vonageApiLatencyMs: networkDiag.vonageApiLatencyMs,
    audioContextState: probeAudioContext(),
    timestamp: Date.now(),
  };
}

export async function getNetworkDiagnosticsOnError(): Promise<NetworkErrorDiagnosticsData> {
  const network = getNetworkStatus();
  let alloApi: NetworkErrorDiagnosticsData['alloApi'] = 'error';
  let alloApiLatencyMs: number | null = null;

  const [, vonageProbe] = await Promise.all([
    (async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const start = Date.now();

        const response = await fetch(window.location.origin, {
          method: 'HEAD',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        alloApiLatencyMs = Date.now() - start;
        alloApi = response.ok ? 'reachable' : 'unreachable';
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          alloApi = 'timeout';
        } else {
          alloApi = 'error';
        }
      }
    })(),
    probeVonageApi(),
  ]);

  return {
    network,
    alloApi,
    alloApiLatencyMs,
    vonageApi: vonageProbe.status,
    vonageApiLatencyMs: vonageProbe.latencyMs,
    timestamp: Date.now(),
  };
}

export function flattenCallDiagnostics(diagnostics: CallDiagnosticsData): Record<string, unknown> {
  const flattened: Record<string, unknown> = {};

  if (diagnostics.network) {
    flattened.networkIsOnline = diagnostics.network.isOnline;
    flattened.networkConnectionType = diagnostics.network.connectionType;
    flattened.networkQuality = diagnostics.network.quality;
    if (diagnostics.network.downlink !== undefined) {
      flattened.networkDownlink = diagnostics.network.downlink;
    }
    if (diagnostics.network.rtt !== undefined) {
      flattened.networkRtt = diagnostics.network.rtt;
    }
    if (diagnostics.network.networkType !== undefined) {
      flattened.networkNetworkType = diagnostics.network.networkType;
    }
    if (diagnostics.network.saveData !== undefined) {
      flattened.networkSaveData = diagnostics.network.saveData;
    }
  }

  if (diagnostics.audio) {
    flattened.audioMicrophoneId = diagnostics.audio.microphoneId;
    flattened.audioMicrophoneLabel = diagnostics.audio.microphoneLabel;
    flattened.audioSpeakerId = diagnostics.audio.speakerId;
    flattened.audioSpeakerLabel = diagnostics.audio.speakerLabel;
    flattened.audioMicrophoneCount = diagnostics.audio.microphoneCount;
    flattened.audioSpeakerCount = diagnostics.audio.speakerCount;
  }

  flattened.diagnosticsTimestamp = diagnostics.timestamp;

  return flattened;
}

export function flattenAudioOutputDiagnostics(
  diagnostics: AudioOutputDiagnostics,
): Record<string, unknown> {
  return {
    audioOutputAudioElementExists: diagnostics.audioElementExists,
    audioOutputAudioElementPaused: diagnostics.audioElementPaused,
    audioOutputAudioElementMuted: diagnostics.audioElementMuted,
    audioOutputAudioElementVolume: diagnostics.audioElementVolume,
    audioOutputAudioElementHasSrcObject: diagnostics.audioElementHasSrcObject,
    audioOutputAudioElementSinkId: diagnostics.audioElementSinkId,
    audioOutputAudioReceiverCount: diagnostics.audioReceiverCount,
    audioOutputAudioReceiverTrackEnabled: diagnostics.audioReceiverTrackEnabled,
    audioOutputAudioReceiverTrackReadyState: diagnostics.audioReceiverTrackReadyState,
    audioOutputAudioReceiverTrackMuted: diagnostics.audioReceiverTrackMuted,
    audioOutputEarmuffed: diagnostics.earmuffed,
  };
}

export function flattenMediaTimeoutDiagnostics(
  diagnostics: MediaTimeoutDiagnosticsData,
): Record<string, unknown> {
  return {
    mtMicCanAcquire: diagnostics.mic.canAcquire,
    mtMicError: diagnostics.mic.error,
    mtMicPermissionState: diagnostics.mic.permissionState,
    mtMicLatencyMs: diagnostics.mic.latencyMs,
    mtIceCanGatherCandidates: diagnostics.ice.canGatherCandidates,
    mtIceCandidateTypes: diagnostics.ice.candidateTypes.join(','),
    mtIceGatheringTimeMs: diagnostics.ice.gatheringTimeMs,
    mtIceError: diagnostics.ice.error,
    mtNetworkIsOnline: diagnostics.network.isOnline,
    mtNetworkConnectionType: diagnostics.network.connectionType,
    mtNetworkQuality: diagnostics.network.quality,
    mtNetworkDownlink: diagnostics.network.downlink,
    mtNetworkRtt: diagnostics.network.rtt,
    mtAlloApi: diagnostics.alloApi,
    mtAlloApiLatencyMs: diagnostics.alloApiLatencyMs,
    mtVonageApi: diagnostics.vonageApi,
    mtVonageApiLatencyMs: diagnostics.vonageApiLatencyMs,
    mtAudioContextState: diagnostics.audioContextState,
    mtTimestamp: diagnostics.timestamp,
  };
}
